import axios from "axios"
import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Outlet, useNavigate } from "react-router"

import { useAppSpinnerContext, useMessageBoxContext, useAppProcessingContext } from "hooks"

import { logout } from "reduxStore/reducers/auth/authSlice"
import { setAppOnIdle } from "reduxStore/reducers/app/appSlice"

import { adminRoutes, routes } from "routing/routes"

import { Button } from "components"

import { notValidTokenCodes } from "scripts/generalVariables"

const AxiosProvider = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { isAdminMode } = useSelector(store => store.app)
    const { showMB, hideMB, showMBError } = useMessageBoxContext()
    const { showProcess, hideProcess, stopProcess, errorProcess } = useAppProcessingContext()
    const { showSpinner, hideSpinner } = useAppSpinnerContext()

    useEffect(() => {
        const reqInterceptor = (config) => {
            if (config.appProcessing) showProcess()
            if (config.appSpinner) showSpinner()
            return config
        }

        const reqErrInterceptor = error => {
            if (error.config.appProcessing) errorProcess()
            if (error.config.appSpinner) hideSpinner()

            return Promise.reject(error)
        }

        const resInterceptor = response => {
            if (response.config.appProcessing) response.config.saveProcessing ? hideProcess() : stopProcess()
            if (response.config.appSpinner) hideSpinner()
            return response
        }

        const resErrInterceptor = error => {
            if (error.config.appProcessing) errorProcess()
            if (error.config.appSpinner) hideSpinner()
            if (error.response?.data) {
                if (error.response.data.code === 'CODE_STILL_NOT_IDENTIFIED') {
                    showMBError(error.response.data.errors || 'DEV ERROR')
                } else if (notValidTokenCodes.includes(error.response.data.code)) {
                    dispatch(logout())
                        .then(() => { dispatch(setAppOnIdle(false)) })
                        .catch((error) => { console.error('ERROR_LOGIN_OUT', error) })
                        .finally(() => {
                            navigate(isAdminMode ? adminRoutes.login.path : routes.login.path)
                            showMB({
                                title: 'La sesión expiró',
                                content: 'Por favor inicia sesión de nuevo',
                                buttons: <Button onClick={hideMB}>Entendido</Button>
                            })
                            return Promise.reject(error)
                        })
                } else if (error.config.catchErrors && error.response.data.errors) {
                    showMBError(error.response.data.errors)
                }
            } else if (error.config.catchNetworkError !== false) {
                showMBError()
            }

            return Promise.reject(error)
        }

        const requestInterceptor = axios.interceptors.request.use(reqInterceptor, reqErrInterceptor)
        const responseInterceptor = axios.interceptors.response.use(resInterceptor, resErrInterceptor)

        return () => {
            axios.interceptors.request.eject(requestInterceptor)
            axios.interceptors.response.eject(responseInterceptor)
        }

        // eslint-disable-next-line
    }, [])

    return <Outlet />
}

export default AxiosProvider